import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { subscribeWithSelector } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import sqliteStorage from "./sqliteStorage";

const generateId = () => uuidv4().replace(/-/g, "").toUpperCase();

const EMPTY_CART = {
  items: [],
  couponInput: [],
  advancePayment: 0,
  total: 0,
  tax: 0,
  discount: 0,
  paid: 0,
  change: 0,
  totalQty: 0,
  roundOff: 0,
  payments: [],
  redemptionPoints: 0,
  accumulationPoints: 0,
  creditAmount: 0,
  docType: "Invoice",
  sOrderID: "",
  referenceId: "",
  giftCardRefId: "",
  couponRefId: "",
  customer: null,
  salesRepId: null,
  cardPaymentData: {},
  documentno: "",
  userName: "",
  tillName: "",
  subTotal: 0,
  itemsSummery: {},
  amountInWords: "",
  groupedItemsByTaxRate: {},
  serviceCharge: 0,
  tip: 0,
  deiveryCharge: 0,
  packingCharge: 0,
  others: 0,
  refundAmount: 0,
  totalStd: 0,
  layAway: "N",
};

const useCartStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // State
        cart: { ...EMPTY_CART },
        selectedProductInCart: {},
        exchangeCart: null,
        _hydrated: false,

        // Actions — setCart supports functional updater like useState: setCart(prev => ({...prev, field}))
        // Always merges with EMPTY_CART to ensure array/object fields (payments, items, etc.) are never undefined
        setCart: (cartOrFn) => {
          if (typeof cartOrFn === "function") {
            set((state) => ({ cart: { ...EMPTY_CART, ...cartOrFn(state.cart) } }));
          } else {
            set({ cart: { ...EMPTY_CART, ...cartOrFn } });
          }
        },

        setSelectedProductInCart: (product) => set({ selectedProductInCart: product }),

        setExchangeCart: (cartOrFn) => {
          if (typeof cartOrFn === "function") {
            set((state) => ({ exchangeCart: cartOrFn(state.exchangeCart) }));
          } else {
            set({ exchangeCart: cartOrFn });
          }
        },

        addItem: (item) =>
          set((state) => {
            const items = [...state.cart.items, item];
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
            };
          }),

        removeItem: (lineId) =>
          set((state) => {
            const items = state.cart.items.filter((i) => i.lineId !== lineId);
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return {
              cart: { ...state.cart, items, total, totalQty, tax },
              selectedProductInCart: {},
            };
          }),

        updateItemQty: (lineId, newQty) =>
          set((state) => {
            const items = state.cart.items.map((item) => {
              if (item.lineId !== lineId) return item;
              const weight = newQty;
              const nettotal = item.salePrice * weight;
              const taxAmount =
                item.taxRate > 0
                  ? item.taxFlag === "Y"
                    ? (nettotal * item.taxRate) / 100
                    : nettotal - nettotal / (1 + item.taxRate / 100)
                  : 0;
              return { ...item, weight, nettotal, total: nettotal, taxAmount };
            });
            const total = items.reduce((sum, i) => sum + (i.nettotal || 0), 0);
            const totalQty = items.reduce((sum, i) => sum + Math.abs(i.weight || 0), 0);
            const tax = items.reduce((sum, i) => sum + (i.taxAmount || 0), 0);
            return { cart: { ...state.cart, items, total, totalQty, tax } };
          }),

        clearCart: (preserveFields = {}) =>
          set({
            cart: {
              ...EMPTY_CART,
              sOrderID: generateId(),
              referenceId: generateId(),
              giftCardRefId: generateId(),
              couponRefId: generateId(),
              ...preserveFields,
            },
            selectedProductInCart: {},
          }),

        // Derived selectors
        getTotal: () => get().cart.total || 0,
        getItemCount: () => get().cart.items?.length || 0,
        getTotalQty: () => get().cart.totalQty || 0,
      }),
      {
        name: "cartObj",
        storage: createJSONStorage(() => sqliteStorage),
        skipHydration: true,
        partialize: (state) => ({ cart: state.cart, exchangeCart: state.exchangeCart }),
        merge: (persisted, current) => {
          const cart = persisted?.cart ? { ...EMPTY_CART, ...persisted.cart } : current.cart;
          // Ensure cart always has valid IDs (first open / web mode starts with empty IDs)
          if (!cart.sOrderID) cart.sOrderID = generateId();
          if (!cart.referenceId) cart.referenceId = generateId();
          if (!cart.giftCardRefId) cart.giftCardRefId = generateId();
          if (!cart.couponRefId) cart.couponRefId = generateId();
          // documentno is set by Core/index.js on POS mount (needs tillData + documentSequence)
          return {
            ...current,
            cart,
            exchangeCart: persisted?.exchangeCart ?? current.exchangeCart,
            _hydrated: true,
          };
        },
      }
    )
  )
);

export { EMPTY_CART };
export default useCartStore;
